/**
 * The website's search landing pages: one page per thing a transporter types into a search engine
 * ("transport management software", "LR bilty software", "transport billing software", …), each
 * answering that query with what the product actually does.
 *
 * Every claim here must be one `features.ts` already makes — these pages reword the product for a
 * search, they never promise more than it does. The website renders them (`/` + `slug`), lists them
 * in its sitemap and footer, and turns `faqs` into FAQPage JSON-LD.
 *
 * `title` is the search-result title before " | <product name>", so keep it under ~45 characters;
 * `description` is the meta description, kept under ~160.
 */
import { pricing, product } from './identity.js';
const name = product.name;
const trial = `${pricing.freeTrialDays}-day free trial`;
export const searchPages = [
    {
        slug: 'transport-management-software',
        title: 'Transport management software for India',
        label: 'Transport management software',
        eyebrow: 'Transport management software',
        heading: 'Transport management software built for Indian transporters.',
        description: `${name} runs trips, LR and bilty, billing, party ledgers, fuel, toll, GPS and job cards in one workspace — on Windows, macOS, Android and in the browser. ${trial}.`,
        sections: [
            {
                heading: 'From booking to POD in one record',
                body: 'A trip carries its party, truck, driver, route, fare and expenses, and the paperwork it needs is generated from it — so the office never retypes a load into three registers.',
                bullets: [
                    'Trips and sub-trips from booking through delivery and POD collection',
                    'Fare filled from the rate card agreed with the party or vehicle owner',
                    'Margin on every trip, after fuel, toll, driver and other expenses',
                    'Pending work at a glance: PODs not received, loading slips not raised, trips not closed',
                ],
            },
            {
                heading: 'Billing and accounts that follow the trips',
                body: 'Bills, receipts and payments are matched to the trips they belong to, and party balances come from the same ledger the trips post to.',
                bullets: [
                    'Invoices, receipts, payment vouchers and party settlements',
                    'GST filing, e-invoice and e-way bill tools, and a TDS register',
                    'Party ledgers with outstanding and ageing, sent by WhatsApp or email',
                    'Tally export, balance sheet, and profit and loss',
                ],
            },
            {
                heading: 'Works when the internet does not',
                body: 'The desktop app writes to a local database first and syncs when the connection returns, so weak signal at the office or depot never stops billing or dispatch.',
                bullets: [
                    'Offline-first desktop app for Windows and macOS',
                    'The same workspace in the browser, with nothing to install',
                    'Android apps for the office and for your drivers',
                    'Role-based permissions for every person on the team',
                ],
            },
        ],
        faqs: [
            {
                question: 'What is transport management software?',
                answer: `Software that keeps a transport business's trips, vehicles, drivers, parties, documents and money in one place. In ${name} a trip is the centre: its LR, expenses, bill, payment and margin all hang off it.`,
            },
            {
                question: `Who is ${name} for?`,
                answer: 'Fleet owners, transport contractors and logistics companies in India that run their own trucks, hire market vehicles, or both. It is priced on fleet size, with hired trips counted alongside own trucks, so a small fleet, a large one and a broker each pay for what they run.',
            },
            {
                question: 'Does it work without internet?',
                answer: 'Yes. The desktop app keeps a local database and syncs when the connection returns. The web app needs a connection while you work.',
            },
        ],
        shot: {
            slug: 'trips',
            alt: 'The trips list showing status, date, truck, driver, route, distance, party, and fare for every trip.',
        },
    },
    {
        slug: 'fleet-management-software',
        title: 'Fleet management software for trucks',
        label: 'Fleet management software',
        eyebrow: 'Fleet management software',
        heading: 'Fleet management software for truck owners.',
        description: `Track every truck's trips, documents, renewals, loans, tyres, service, fuel and live GPS position — and what it earned against what it cost. ${trial}.`,
        sections: [
            {
                heading: 'Every vehicle with its papers attached',
                body: 'Each truck keeps its documents, insurance, permits, loan and hypothecation terms, and reminds you before anything expires.',
                bullets: [
                    'Renewal reminders for insurance, PUC, permits and e-way bills',
                    'Loan EMI schedules matched to bank debits',
                    'Purchase and sale with the money on both sides',
                    'Driver history and toll history on every truck',
                ],
            },
            {
                heading: 'Live tracking beside the trip',
                body: 'Positions come from your GPS provider or the driver’s phone, and a trip advances as the truck crosses its route.',
                bullets: [
                    'Live map and list views of the fleet',
                    'Integrations with iAlert and WheelsEye tracking',
                    'Pinned routes the driver’s phone navigates stop by stop',
                    'A fleet wall for the office TV',
                ],
            },
            {
                heading: 'What each truck really earns',
                body: 'Trip income, fuel, toll, repairs, tyres, EMIs and running costs land on the same vehicle, so profit per truck is a report rather than a guess.',
                bullets: [
                    'Vehicle and vehicle-financial summaries',
                    'Cost per km, lane profitability and fleet utilisation',
                    'Job cards and tyre history costed against the vehicle',
                    'Fuel-card and NETC toll spend reconciled to trips',
                ],
            },
        ],
        faqs: [
            {
                question: 'Can I see live GPS location of my trucks?',
                answer: 'Yes, from a supported tracking provider (such as iAlert or WheelsEye) or from the driver’s phone through the driver app.',
            },
            {
                question: 'Does it remind me about insurance and permit renewals?',
                answer: 'Yes. Insurance, PUC, permit and e-way bill expiry, and preventive service due dates, all raise reminders before they fall due.',
            },
            {
                question: 'Can I see profit per truck?',
                answer: 'Yes. Trip income and every cost recorded against a vehicle — fuel, toll, repairs, tyres, EMIs and running costs — roll up into vehicle summaries and cost per km.',
            },
        ],
        shot: {
            slug: 'vehicles',
            alt: 'The vehicles list showing fleet records with document validity, status, and activity.',
        },
    },
    {
        slug: 'lr-bilty-software',
        title: 'LR and bilty software for transporters',
        label: 'LR and bilty software',
        eyebrow: 'Lorry receipt (LR) and bilty software',
        heading: 'Make the LR, bilty and challan from the trip.',
        description: `Generate lorry receipts (LR / bilty / consignment notes), challans and loading slips straight from the trip, print them on one sheet, and share them as PDF on WhatsApp. ${trial}.`,
        sections: [
            {
                heading: 'Trip documents without retyping',
                body: 'The consignor, consignee, truck, route, goods and freight are already on the trip, so the LR, challan and loading slip are printed from it instead of written by hand.',
                bullets: [
                    'Lorry receipt / bilty / consignment note from the trip',
                    'Challan and loading slip for the same load',
                    'Each copy fits one sheet',
                    'Your firm’s own look applied to every printed document',
                ],
            },
            {
                heading: 'Corrections kept, not lost',
                body: 'When a document is edited after it was printed, the earlier version is kept, so you can always see what was issued.',
                bullets: [
                    'Version history on LRs, challans and slips',
                    'Attach the signed POD and photos to the trip',
                    'Pending-POD report so nothing is left unbilled',
                ],
            },
            {
                heading: 'Share from the office or the phone',
                body: 'Send the bilty, invoice or statement PDF straight to the party.',
                bullets: [
                    'Share bilty and invoice PDFs from the Android app',
                    'Send documents by WhatsApp or email',
                    'The bill raised from the same trip, with GST and e-way bill tools',
                ],
            },
        ],
        faqs: [
            {
                question: 'What is an LR or bilty?',
                answer: 'A lorry receipt (LR), also called a bilty or consignment note, is the document a transporter issues when goods are loaded, recording the consignor, consignee, goods, truck and freight.',
            },
            {
                question: 'Can I print LRs in my own format?',
                answer: `${name} applies your organisation’s document style to every LR, challan and invoice it generates, and the LR has one-sheet layout options.`,
            },
            {
                question: 'Can I share a bilty on WhatsApp?',
                answer: 'Yes. Bilty, invoice and statement PDFs can be shared from the desktop, the browser or the Android app.',
            },
        ],
        shot: {
            slug: 'trip-details',
            alt: 'A trip’s details with its party, truck, driver, route, fare, expenses and documents.',
        },
    },
    {
        slug: 'transport-billing-software',
        title: 'Transport billing software with GST',
        label: 'Transport billing software',
        eyebrow: 'Transport billing software',
        heading: 'Bill every trip, collect every rupee.',
        description: `GST invoices raised from trips, e-invoice and e-way bill tools, TDS register, part payments and reminders by WhatsApp — transport billing that ties back to the trip. ${trial}.`,
        sections: [
            {
                heading: 'Invoices from the trips they cover',
                body: 'Pick the trips, raise the bill, and the freight and charges come from the trips themselves.',
                bullets: [
                    'GST invoices raised from one or many trips',
                    'e-invoice and e-way bill portal tools',
                    'TDS register checked against Form 26AS',
                    'Invoice corrections kept as versions',
                ],
            },
            {
                heading: 'Payments matched to bills',
                body: 'Receipts are applied to the bills and trips they pay, including part payments and on-account credit from earlier payments.',
                bullets: [
                    'Receipts, payment vouchers and credit applied across documents',
                    'Bank statement matching that learns from your decisions',
                    'Outstanding and ageing per party',
                ],
            },
            {
                heading: 'Collections that chase themselves',
                body: 'A collections view ranks which parties are worth calling today and sends the payment reminder for you.',
                bullets: [
                    'Payment reminders by WhatsApp or email',
                    'Shareable party statement links with password and expiry',
                    'Tally export when the accountant needs it',
                ],
            },
        ],
        faqs: [
            {
                question: 'Does it make GST invoices for transport?',
                answer: 'Yes. Invoices are raised from trips with GST, and the billing section includes GST filing, e-invoice and e-way bill tools.',
            },
            {
                question: 'Can I export to Tally?',
                answer: 'Yes. Vouchers export to Tally, and the balance sheet and profit and loss are available in the app.',
            },
            {
                question: 'Does it handle part payments?',
                answer: 'Yes. A receipt can be split across several bills or trips, and an over-payment is kept as credit and applied to a later bill.',
            },
        ],
        shot: {
            slug: 'outstanding-analysis',
            alt: 'The outstanding analysis showing what each party owes, by age.',
        },
    },
    {
        slug: 'transport-accounting-software',
        title: 'Transport accounting software',
        label: 'Transport accounting software',
        eyebrow: 'Transport accounting software',
        heading: 'Accounts that come straight from your trips.',
        description: `Party ledgers, settlements, driver accounts, cash and bank, loans, P&L and balance sheet — transport accounting built on one ledger, with Tally export. ${trial}.`,
        sections: [
            {
                heading: 'One ledger behind everything',
                body: 'Trips, bills, receipts, vouchers and settlements all post to the same ledger, so every report agrees with every other.',
                bullets: [
                    'Accounts for cash, bank, fuel card and loans',
                    'Income and expense heads on every record',
                    'Capital accounts for partners’ money',
                    'Balance sheet and profit and loss',
                ],
            },
            {
                heading: 'Party and driver settlements',
                body: 'Collect a period’s trips and payments into one basket and close it at parity — for parties, vehicle owners and drivers alike.',
                bullets: [
                    'Party settlements with trips and payments together',
                    'Driver salary and advances settled in one place',
                    'Market-vehicle owner payments against broker trips',
                ],
            },
            {
                heading: 'Hand it to your CA',
                body: 'Export the books when your accountant asks, without re-entering anything.',
                bullets: [
                    'Tally export',
                    'Party ledger exports and shareable statements',
                    'Trip, expense, outstanding, salary, loan and tax reports',
                ],
            },
        ],
        faqs: [
            {
                question: 'Do I still need Tally?',
                answer: `Many transporters keep Tally for their CA. ${name} keeps the day-to-day accounts and exports to Tally when needed.`,
            },
            {
                question: 'Can I keep driver accounts (hesab)?',
                answer: 'Yes. Driver advances, expenses and salary are settled together, and drivers can record expenses from the driver app.',
            },
            {
                question: 'Can I import my old data?',
                answer: 'Yes. Moving from another platform is part of onboarding: tell us what you use today and we work through the move with you.',
            },
        ],
        shot: {
            slug: 'action-center',
            alt: 'The owner action centre ranking parties by overdue value, oldest overdue days, pending trips, and expected collection.',
        },
    },
    {
        slug: 'truck-maintenance-software',
        title: 'Truck maintenance and tyre software',
        label: 'Truck maintenance software',
        eyebrow: 'Truck maintenance software',
        heading: 'Job cards, parts and tyres on the same ledger as the fleet.',
        description: `Raise workshop job cards against a truck, issue parts from FIFO-costed stock, track every tyre by serial number and get service-due reminders. ${trial}.`,
        sections: [
            {
                heading: 'Workshop job cards',
                body: 'Every repair and service is a job card against the vehicle, with its labour, parts and photos — raised on the desktop, in the browser or on the phone.',
                bullets: [
                    'Job cards on desktop, web and Android',
                    'Service schedules that advance when a service job card closes',
                    'Repair cost rolled into the vehicle’s running cost',
                ],
            },
            {
                heading: 'Parts and tyres',
                body: 'Stock is costed FIFO, and tyres are followed one by one from purchase to scrap.',
                bullets: [
                    'Parts inventory at FIFO cost, with purchase bills',
                    'Tyres tracked by serial number — fitted, rotated, retreaded, scrapped',
                    'Fitment by position on each truck',
                ],
            },
        ],
        faqs: [
            {
                question: 'Can I track tyres by serial number?',
                answer: 'Yes. Each tyre is its own record with its position, fitments, retreads and scrap.',
            },
            {
                question: 'Can mechanics raise job cards on a phone?',
                answer: 'Yes. Job cards can be raised, edited and photographed from the Android app.',
            },
        ],
    },
    {
        slug: 'fuel-and-toll-management',
        title: 'Fuel card and FASTag toll management',
        label: 'Fuel and toll management',
        eyebrow: 'Fuel and toll management',
        heading: 'Every litre and every toll, on the trip that used it.',
        description: `BPCL and Reliance fuel-card balances and limits, pump credit-bill imports, and NETC FASTag toll transactions reconciled against your trips. ${trial}.`,
        sections: [
            {
                heading: 'Fuel cards and pump bills',
                body: 'See card balances, set limits and PINs, and import the pump’s credit bill instead of typing it.',
                bullets: [
                    'BPCL and Reliance fuel-card balances, limits and transactions',
                    'Pump credit-bill PDF imports',
                    'Fuel recharges matched to bank payments',
                ],
            },
            {
                heading: 'NETC FASTag tolls',
                body: 'Toll transactions are recorded against the trips that incurred them, with a toll history per vehicle.',
                bullets: [
                    'NETC toll transactions per vehicle',
                    'Tolls reconciled against the trips they belong to',
                    'Leakage checks across trips and spend',
                ],
            },
        ],
        faqs: [
            {
                question: 'Which fuel cards are supported?',
                answer: 'BPCL and Reliance fuel cards, plus pump credit-bill PDF imports for other pumps.',
            },
            {
                question: 'Does it import FASTag toll transactions?',
                answer: 'Yes. NETC toll transactions are recorded against the vehicle and the trip they belong to.',
            },
        ],
    },
];
//# sourceMappingURL=searchPages.js.map